"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Phone } from "lucide-react";
import { SiWhatsapp } from "react-icons/si";

const WHATSAPP_NUMBER = "971551927985";
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}`;

const actions = [
  {
    id: "whatsapp",
    label: "WhatsApp",
    icon: (
      <SiWhatsapp
        className="w-5 h-5"
        role="presentation"
        aria-hidden="true"
        focusable="false"
      />
    ),
    bg: "#25D366",
    href: WHATSAPP_URL,
    external: true,
  },
  {
    id: "contact",
    label: "Contact Us",
    icon: <Phone className="w-5 h-5" aria-hidden="true" focusable="false" />,
    bg: "#112F6B",
    href: "/contact",
    external: false,
  },
];

export function FloatingActions() {
  const [open, setOpen] = useState(true);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Start open on load, then: close after 15 s → wait 15 s → open 15 s → repeat
  useEffect(() => {
    let cancelled = false;
    let waitTimer: ReturnType<typeof setTimeout> | undefined;
    let closeTimer: ReturnType<typeof setTimeout> | undefined;

    const CYCLE_MS = 15_000;

    const runCycle = () => {
      waitTimer = setTimeout(() => {
        if (cancelled) return;
        setOpen(true);
        closeTimer = setTimeout(() => {
          if (cancelled) return;
          setOpen(false);
          runCycle();
        }, CYCLE_MS);
      }, CYCLE_MS);
    };

    // First paint is open; after 15 s close, then same loop as before
    closeTimer = setTimeout(() => {
      if (cancelled) return;
      setOpen(false);
      runCycle();
    }, CYCLE_MS);

    return () => {
      cancelled = true;
      if (waitTimer !== undefined) clearTimeout(waitTimer);
      if (closeTimer !== undefined) clearTimeout(closeTimer);
    };
  }, []);

  return (
    <div
      ref={ref}
      className="fixed left-8 bottom-8 z-50 flex flex-col-reverse items-start gap-3"
      aria-label="Quick contact options"
    >
      {/* ── Action items — slide up from the trigger ── */}
      <AnimatePresence>
        {open &&
          actions.map((action, i) => (
            <motion.div
              key={action.id}
              initial={{ opacity: 0, x: -16, scale: 0.9 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: -16, scale: 0.9 }}
              transition={{
                duration: 0.28,
                delay: i * 0.06,
                ease: "easeOut" as const,
              }}
              className="flex items-center gap-3"
            >
              {/* Icon circle */}
              <div
                className="w-13 h-13 rounded-full flex items-center justify-center text-white shadow-lg flex-shrink-0 ring-2 ring-white"
                style={{ backgroundColor: action.bg, width: 52, height: 52 }}
              >
                {action.icon}
              </div>

              {/* Label pill */}
              {action.external ? (
                <a
                  href={action.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setOpen(false)}
                  className="bg-white text-neutral-800 font-semibold text-sm px-5 py-3 rounded-2xl shadow-lg border border-neutral-100 whitespace-nowrap hover:shadow-xl hover:border-neutral-200 transition-all duration-200 ring-1 ring-black/5"
                >
                  {action.label}
                </a>
              ) : (
                <Link
                  href={action.href}
                  prefetch={false}
                  onClick={() => setOpen(false)}
                  className="bg-white text-neutral-800 font-semibold text-sm px-5 py-3 rounded-2xl shadow-lg border border-neutral-100 whitespace-nowrap hover:shadow-xl hover:border-neutral-200 transition-all duration-200 ring-1 ring-black/5"
                >
                  {action.label}
                </Link>
              )}
            </motion.div>
          ))}
      </AnimatePresence>

      {/* ── Trigger button ── */}
      <motion.button
        onClick={() => setOpen((v) => !v)}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.94 }}
        aria-label={open ? "Close contact options" : "Open contact options"}
        aria-expanded={open}
        className="w-14 h-14 rounded-full flex items-center justify-center text-white shadow-xl ring-4 ring-white transition-all duration-300"
        style={{
          background: open
            ? "linear-gradient(135deg, #374151, #111827)"
            : "linear-gradient(135deg, #2BA6E6, #112F6B)",
        }}
      >
        <AnimatePresence mode="wait" initial={false}>
          {open ? (
            <motion.span
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <X className="w-6 h-6" aria-hidden="true" focusable="false" />
            </motion.span>
          ) : (
            <motion.span
              key="open"
              initial={{ rotate: 90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <MessageCircle className="w-6 h-6" aria-hidden="true" focusable="false" />
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>
    </div>
  );
}
